import { createFileRoute, Link } from '@tanstack/react-router'
import { Button, Card, CardContent, CardDescription, CardTitle, Skeleton } from '@lead-magnifier/ui'
import { Plus, Calendar } from 'lucide-react'
import { useEvents, useArchiveEvent } from '@/hooks/use-events'
import { EventCard } from '@/components/event-card'
import { useToast } from '@lead-magnifier/ui'

export const Route = createFileRoute('/_authenticated/events/')({
  component: EventsPage,
})

function EventsPage() {
  const { data: events, isLoading, error } = useEvents()
  const archiveMutation = useArchiveEvent()
  const { toast } = useToast()

  const handleArchive = async (eventId: string) => {
    try {
      await archiveMutation.mutateAsync(eventId)
      toast({
        title: 'Event archiviert',
        description: 'Das Event wurde erfolgreich archiviert.',
      })
    } catch {
      toast({
        title: 'Fehler',
        description: 'Das Event konnte nicht archiviert werden.',
        variant: 'destructive',
      })
    }
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Events</h1>
        </div>
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-destructive">Fehler beim Laden der Events.</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Events</h1>
          <p className="text-muted-foreground mt-1">
            Verwalte deine Lead-Formulare und Events
          </p>
        </div>
        <Link to="/events/new">
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            Neues Event
          </Button>
        </Link>
      </div>

      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardContent className="p-4 space-y-4">
                <div className="flex gap-4">
                  <Skeleton className="w-24 h-24 rounded-lg" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-5 w-3/4" />
                    <Skeleton className="h-4 w-1/2" />
                  </div>
                </div>
                <Skeleton className="h-8 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : events?.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Calendar className="h-12 w-12 text-muted-foreground mb-4" />
            <CardTitle className="mb-2">Noch keine Events</CardTitle>
            <CardDescription className="text-center max-w-sm mb-4">
              Erstelle dein erstes Event, um Leads zu sammeln. Jedes Event hat sein eigenes
              Formular und QR-Code.
            </CardDescription>
            <Link to="/events/new">
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Erstes Event erstellen
              </Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {events?.map((event) => (
            <EventCard
              key={event.id}
              event={event}
              onArchive={handleArchive}
            />
          ))}
        </div>
      )}
    </div>
  )
}
